/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import {
  Button,
  Cell,
  Grid,
  Heading,
  HeadingSection,
  HFlow,
  Icon,
  Modal,
  ModalBody,
  ModalFooter,
  Text,
  Tooltip,
  VFlow,
} from 'bold-ui'
import useSession from 'components/auth/useSession'
import { Box } from 'components/Box'
import theme from 'config/theme'
import { useLoadAtestadoByIdLazyQuery } from 'graphql/hooks.generated'
import { Profissional, TipoAtestadoEnum } from 'graphql/types.generated'
import moment from 'moment'
import { useMemo } from 'react'

import { grupoCboAtestado } from '../../acessos'
import { RegistroLista } from './AtestadosTable'

export interface AtestadosVisualizacaoModalProps {
  registro: RegistroLista
  descricaoAtestadoRecente?: string
  tipoAtestadoRecente?: TipoAtestadoEnum
  handlePrint(): void
  handleModalClose(): void
}

export interface ProfissionalSignProps {
  profissional: Partial<Profissional>
  profissionalCbo: string
}

export default function AtestadosVisualizacaoModal({
  registro,
  descricaoAtestadoRecente,
  tipoAtestadoRecente,
  handlePrint,
  handleModalClose,
}: AtestadosVisualizacaoModalProps) {
  const [executeQuery, { data }] = useLoadAtestadoByIdLazyQuery()
  const { hasCboAuth } = useSession({ fetchPolicy: 'cache-only' })

  useMemo(() => {
    if (!registro.recente) executeQuery({ variables: { id: registro.id } })
  }, [executeQuery, registro.id, registro.recente])

  const tipoAtestado = registro.recente ? tipoAtestadoRecente : data?.atestado.tipo
  const tituloModal =
    tipoAtestado === TipoAtestadoEnum.LICENCA_MATERNIDADE ? 'Visualizar licença maternidade' : 'Visualizar atestado'
  const titulo = tipoAtestado === TipoAtestadoEnum.LICENCA_MATERNIDADE ? 'Licença Maternidade' : 'Atestado'
  const descricao = registro.recente ? descricaoAtestadoRecente : data?.atestado.descricao
  const municipioRegistro = registro?.atendimentoProfissional?.lotacao.municipio
  const dataAtendimentoRegistro = registro?.atendimentoProfissional?.iniciadoEm
  const nomeCboRegistro = registro?.atendimentoProfissional?.lotacao.cbo.nome
  const profissionalRegistro = registro?.atendimentoProfissional?.lotacao.profissional

  const hasPermissionImprimirAtestado = hasCboAuth(grupoCboAtestado.imprimir)

  return (
    <Modal
      style={css`
        width: 41rem;
      `}
      onClose={handleModalClose}
      open
    >
      <ModalBody>
        <HeadingSection level={1} title={tituloModal} />
        <Box>
          {tipoAtestado && (
            <VFlow
              style={css`
                margin: 1.5rem 0.5rem;
                text-align: center;
              `}
            >
              <Heading level={3}>{titulo}</Heading>
            </VFlow>
          )}
          <Grid>
            <Cell
              size={12}
              style={css`
                margin: 1rem 2rem;
              `}
            >
              <VFlow
                style={css`
                  text-align: justify;
                `}
              >
                <Text dangerouslySetInnerHTML={{ __html: descricao }} />
              </VFlow>
            </Cell>
            <Cell size={3} />
          </Grid>
          <VFlow
            style={css`
              justify-content: center;
              align-items: center;
            `}
            vSpacing={0}
          >
            {municipioRegistro?.nome.titleCase() +
              ' - ' +
              municipioRegistro?.uf.sigla +
              ', ' +
              moment(dataAtendimentoRegistro).format('DD [de] MMMM [de] YYYY').toLowerCase()}
            <ProfissionalSign profissional={profissionalRegistro} profissionalCbo={nomeCboRegistro} />
          </VFlow>
        </Box>
      </ModalBody>
      {hasPermissionImprimirAtestado && (
        <ModalFooter>
          <HFlow justifyContent='flex-end'>
            <Tooltip text='Imprimir'>
              <Button size='medium' kind='primary' onClick={handlePrint}>
                <Icon
                  icon='printerOutline'
                  style={css`
                    margin-right: 0.75rem;
                  `}
                />
                Imprimir
              </Button>
            </Tooltip>
          </HFlow>
        </ModalFooter>
      )}
    </Modal>
  )
}

export function ProfissionalSign({ profissional, profissionalCbo }: ProfissionalSignProps) {
  return (
    <VFlow style={styles.sign} vSpacing={0}>
      <Text component='b'>
        {profissional?.nome}
        {profissional?.conselhoClasse &&
          ` - ${profissional.conselhoClasse.sigla} - ${profissional.ufEmissoraConselhoClasse.sigla} ${profissional.numeroConselhoClasse}`}
      </Text>
      {profissionalCbo && <Text> {profissionalCbo}</Text>}
    </VFlow>
  )
}

const styles = {
  sign: css`
    border-top: solid;
    border-top-width: 1px;
    border-top-color: ${theme.pallete.gray.c30};
    align-items: center;
    margin-top: 4rem;
    margin-bottom: 1.5rem;
    font-size: 13px;
  `,
}
